import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { BulletinDialog } from './BulletinDialog';
import { BulletinItem } from './BulletinItem';
import { bulletinsAPI, errMsg } from '../../lib/regulatoryAPI';

const Tile = ({ label, value, testId }) => (
  <Card><CardContent className="pt-5"><p className="text-xs text-muted-foreground">{label}</p><p className="mt-1 text-2xl font-semibold" data-testid={testId}>{value ?? '–'}</p></CardContent></Card>
);

const Sel = ({ value, onChange, options, label, testId }) => (
  <select className="h-9 rounded-md border bg-background px-2 text-sm" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
    <option value="">{label}</option>{options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

const Counts = ({ title, data, testId }) => (
  <Card><CardHeader className="pb-2"><CardTitle className="text-sm">{title}</CardTitle></CardHeader>
    <CardContent className="flex flex-wrap gap-2" data-testid={testId}>
      {Object.keys(data || {}).length ? Object.entries(data).map(([k, v]) => <span key={k} className="rounded-full border px-2.5 py-0.5 text-xs">{k} <b>{v}</b></span>)
        : <span className="text-sm text-muted-foreground">No bulletins yet.</span>}
    </CardContent></Card>
);

export const BulletinDashboard = ({ meta }) => {
  const [dash, setDash] = useState(null);
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ q: '', source: '', topic: '', impact: '', status: '' });
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => Promise.all([bulletinsAPI.dashboard(), bulletinsAPI.list(f)])
    .then(([d, l]) => { setDash(d); setItems(l.items); }).catch((e) => toast.error(errMsg(e))), [f]);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const t = dash?.tiles;
  return (
    <div className="space-y-6 pt-2">
      <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <Tile label="Total" value={t?.total} testId="bulletins-tile-total" />
        <Tile label="New" value={t?.new} testId="bulletins-tile-new" />
        <Tile label="Unread (you)" value={t?.unread} testId="bulletins-tile-unread" />
        <Tile label="High / critical" value={t?.high_critical} testId="bulletins-tile-high" />
        <Tile label="Key dates (30 days)" value={t?.upcoming_30d} testId="bulletins-tile-upcoming" />
        <Tile label="Drafts awaiting review" value={t?.drafts_awaiting_review} testId="bulletins-tile-drafts" />
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <Counts title="By source" data={dash?.by_source} testId="bulletins-by-source" />
        <Counts title="By topic" data={dash?.by_topic} testId="bulletins-by-topic" />
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Upcoming deadlines</CardTitle></CardHeader>
          <CardContent className="space-y-1.5 text-sm" data-testid="bulletins-upcoming">
            {(dash?.upcoming || []).length ? dash.upcoming.map((u, i) => <div key={i} className="flex justify-between gap-2"><span className="truncate">{u.label} <span className="text-muted-foreground">· {u.source}</span></span><b className="shrink-0">{u.date}</b></div>)
              : <span className="text-muted-foreground">No key dates in the next 30 days.</span>}
          </CardContent></Card>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input className="pl-8" placeholder="Search bulletins" value={f.q} onChange={(e) => set('q')(e.target.value)} data-testid="bulletins-search" /></div>
        <Sel value={f.source} onChange={set('source')} options={meta.sources} label="All sources" testId="bulletins-filter-source" />
        <Sel value={f.topic} onChange={set('topic')} options={meta.topics} label="All topics" testId="bulletins-filter-topic" />
        <Sel value={f.impact} onChange={set('impact')} options={meta.impacts} label="All impacts" testId="bulletins-filter-impact" />
        <Sel value={f.status} onChange={set('status')} options={meta.statuses} label="Active (not archived)" testId="bulletins-filter-status" />
        {meta.can_manage && <Button onClick={() => setEdit({})} data-testid="bulletins-add-btn"><Plus className="mr-2 h-4 w-4" />Add bulletin</Button>}
      </div>
      <div className="space-y-3" data-testid="bulletins-feed">
        {items.length ? items.map((b) => <BulletinItem key={b.id} b={b} meta={meta} onChanged={load} onEdit={() => setEdit(b)} />)
          : <p className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground" data-testid="bulletins-empty">No bulletins match.</p>}
      </div>
      {edit && <BulletinDialog meta={meta} initial={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
};
